/**
 * sl-9qm.4.2: BuilderComponentWrapper
 *
 * HOC that wraps ViewComponents in builder mode with
 * drag handle, selection border, and click-to-select behavior.
 *
 * sl-bpu6.6.2: Optional `onDragStart` makes the small drag handle (not the
 * whole wrapper) the HTML5 drag source. Scoping the drag source keeps text
 * selection + nested interactive children working everywhere else.
 */
import type { DragEvent, FC, ReactNode } from 'react';

import * as styles from './styles.css';

export interface BuilderComponentWrapperProps {
  children: ReactNode;
  selected: boolean;
  hovered: boolean;
  onSelect: () => void;
  runtimeIndicator?: boolean;
  /**
   * sl-bpu6.6.2: Drag-start handler attached to the drag handle only.
   * Undefined = no drag affordance (e.g. when builder mode is shown without
   * a DnD surface).
   */
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
}

export const BuilderComponentWrapper: FC<BuilderComponentWrapperProps> = ({
  children,
  selected,
  onSelect,
  runtimeIndicator,
  onDragStart,
}) => {
  return (
    <div
      className={`${styles.builderWrapper} ${selected ? styles.builderWrapperSelected : ''}`}
      onClick={onSelect}
    >
      <div
        className={styles.dragHandle}
        draggable={onDragStart !== undefined}
        onDragStart={onDragStart}
        data-testid="view-component-drag-handle"
      >
        ⠿
      </div>
      {runtimeIndicator ? (
        <div className={styles.runtimeIndicator} title="Visibility or repeat">
          ◇
        </div>
      ) : null}
      {children}
      {selected && <div className={styles.selectionBorder} />}
    </div>
  );
};
